/** Built-in color palettes shipped with the Tier 3 Group D
 *  swatches feature. Three first-party sets:
 *
 *    - Material (Google) — the original 2014 palette + greys
 *    - Tailwind — 2024 v3 default palette
 *    - Brand — a small Fias-curated set keyed to product surfaces
 *
 *  All built-ins are read-only. The picker lets the user duplicate
 *  one into a user palette before edits — the "Duplicate to my
 *  palettes" affordance writes through `user-palettes-prefs`. */

export interface Palette {
  /** Stable id. For built-ins this is `builtin:<slug>`; for user
   *  palettes the prefs module generates a uuid-ish slug at create
   *  time. Used as the React key and the localStorage cursor. */
  id: string;
  /** Display name. User-renameable for user palettes; immutable
   *  for built-ins (rename UI is gated on `kind === 'user'`). */
  name: string;
  /** Distinguishes built-in vs user, gating delete / rename /
   *  add-color affordances in the picker. */
  kind: 'builtin' | 'user';
  /** Optional named groupings — Material's "Red / Pink / Purple / …"
   *  rows render as titled rows in the swatches grid. User
   *  palettes have a single anonymous group. */
  groups: PaletteGroup[];
}

export interface PaletteGroup {
  /** Group label — rendered as a small caption above the row. May
   *  be empty (`''`) for a flat ungrouped palette. */
  label: string;
  /** `#rrggbb` hex strings in display order. */
  colors: string[];
}

// ─── Material 2014 ──────────────────────────────────────────────

const MATERIAL_RED = [
  '#ffebee',
  '#ffcdd2',
  '#ef9a9a',
  '#e57373',
  '#ef5350',
  '#f44336',
  '#e53935',
  '#d32f2f',
  '#c62828',
  '#b71c1c',
];
const MATERIAL_PINK = [
  '#fce4ec',
  '#f8bbd0',
  '#f48fb1',
  '#f06292',
  '#ec407a',
  '#e91e63',
  '#d81b60',
  '#c2185b',
  '#ad1457',
  '#880e4f',
];
const MATERIAL_PURPLE = [
  '#f3e5f5',
  '#e1bee7',
  '#ce93d8',
  '#ba68c8',
  '#ab47bc',
  '#9c27b0',
  '#8e24aa',
  '#7b1fa2',
  '#6a1b9a',
  '#4a148c',
];
const MATERIAL_DEEP_PURPLE = [
  '#ede7f6',
  '#d1c4e9',
  '#b39ddb',
  '#9575cd',
  '#7e57c2',
  '#673ab7',
  '#5e35b1',
  '#512da8',
  '#4527a0',
  '#311b92',
];
const MATERIAL_INDIGO = [
  '#e8eaf6',
  '#c5cae9',
  '#9fa8da',
  '#7986cb',
  '#5c6bc0',
  '#3f51b5',
  '#3949ab',
  '#303f9f',
  '#283593',
  '#1a237e',
];
const MATERIAL_BLUE = [
  '#e3f2fd',
  '#bbdefb',
  '#90caf9',
  '#64b5f6',
  '#42a5f5',
  '#2196f3',
  '#1e88e5',
  '#1976d2',
  '#1565c0',
  '#0d47a1',
];
const MATERIAL_LIGHT_BLUE = [
  '#e1f5fe',
  '#b3e5fc',
  '#81d4fa',
  '#4fc3f7',
  '#29b6f6',
  '#03a9f4',
  '#039be5',
  '#0288d1',
  '#0277bd',
  '#01579b',
];
const MATERIAL_CYAN = [
  '#e0f7fa',
  '#b2ebf2',
  '#80deea',
  '#4dd0e1',
  '#26c6da',
  '#00bcd4',
  '#00acc1',
  '#0097a7',
  '#00838f',
  '#006064',
];
const MATERIAL_TEAL = [
  '#e0f2f1',
  '#b2dfdb',
  '#80cbc4',
  '#4db6ac',
  '#26a69a',
  '#009688',
  '#00897b',
  '#00796b',
  '#00695c',
  '#004d40',
];
const MATERIAL_GREEN = [
  '#e8f5e9',
  '#c8e6c9',
  '#a5d6a7',
  '#81c784',
  '#66bb6a',
  '#4caf50',
  '#43a047',
  '#388e3c',
  '#2e7d32',
  '#1b5e20',
];
const MATERIAL_LIGHT_GREEN = [
  '#f1f8e9',
  '#dcedc8',
  '#c5e1a5',
  '#aed581',
  '#9ccc65',
  '#8bc34a',
  '#7cb342',
  '#689f38',
  '#558b2f',
  '#33691e',
];
const MATERIAL_LIME = [
  '#f9fbe7',
  '#f0f4c3',
  '#e6ee9c',
  '#dce775',
  '#d4e157',
  '#cddc39',
  '#c0ca33',
  '#afb42b',
  '#9e9d24',
  '#827717',
];
const MATERIAL_YELLOW = [
  '#fffde7',
  '#fff9c4',
  '#fff59d',
  '#fff176',
  '#ffee58',
  '#ffeb3b',
  '#fdd835',
  '#fbc02d',
  '#f9a825',
  '#f57f17',
];
const MATERIAL_AMBER = [
  '#fff8e1',
  '#ffecb3',
  '#ffe082',
  '#ffd54f',
  '#ffca28',
  '#ffc107',
  '#ffb300',
  '#ffa000',
  '#ff8f00',
  '#ff6f00',
];
const MATERIAL_ORANGE = [
  '#fff3e0',
  '#ffe0b2',
  '#ffcc80',
  '#ffb74d',
  '#ffa726',
  '#ff9800',
  '#fb8c00',
  '#f57c00',
  '#ef6c00',
  '#e65100',
];
const MATERIAL_DEEP_ORANGE = [
  '#fbe9e7',
  '#ffccbc',
  '#ffab91',
  '#ff8a65',
  '#ff7043',
  '#ff5722',
  '#f4511e',
  '#e64a19',
  '#d84315',
  '#bf360c',
];
const MATERIAL_BROWN = [
  '#efebe9',
  '#d7ccc8',
  '#bcaaa4',
  '#a1887f',
  '#8d6e63',
  '#795548',
  '#6d4c41',
  '#5d4037',
  '#4e342e',
  '#3e2723',
];
const MATERIAL_GREY = [
  '#fafafa',
  '#f5f5f5',
  '#eeeeee',
  '#e0e0e0',
  '#bdbdbd',
  '#9e9e9e',
  '#757575',
  '#616161',
  '#424242',
  '#212121',
];
const MATERIAL_BLUE_GREY = [
  '#eceff1',
  '#cfd8dc',
  '#b0bec5',
  '#90a4ae',
  '#78909c',
  '#607d8b',
  '#546e7a',
  '#455a64',
  '#37474f',
  '#263238',
];

const MATERIAL: Palette = {
  id: 'builtin:material',
  name: 'Material',
  kind: 'builtin',
  groups: [
    { label: 'Red', colors: MATERIAL_RED },
    { label: 'Pink', colors: MATERIAL_PINK },
    { label: 'Purple', colors: MATERIAL_PURPLE },
    { label: 'Deep Purple', colors: MATERIAL_DEEP_PURPLE },
    { label: 'Indigo', colors: MATERIAL_INDIGO },
    { label: 'Blue', colors: MATERIAL_BLUE },
    { label: 'Light Blue', colors: MATERIAL_LIGHT_BLUE },
    { label: 'Cyan', colors: MATERIAL_CYAN },
    { label: 'Teal', colors: MATERIAL_TEAL },
    { label: 'Green', colors: MATERIAL_GREEN },
    { label: 'Light Green', colors: MATERIAL_LIGHT_GREEN },
    { label: 'Lime', colors: MATERIAL_LIME },
    { label: 'Yellow', colors: MATERIAL_YELLOW },
    { label: 'Amber', colors: MATERIAL_AMBER },
    { label: 'Orange', colors: MATERIAL_ORANGE },
    { label: 'Deep Orange', colors: MATERIAL_DEEP_ORANGE },
    { label: 'Brown', colors: MATERIAL_BROWN },
    { label: 'Grey', colors: MATERIAL_GREY },
    { label: 'Blue Grey', colors: MATERIAL_BLUE_GREY },
  ],
};

// ─── Tailwind v3 default palette ────────────────────────────────

const TW_SHADES = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'];

const TW_SLATE = [
  '#f8fafc',
  '#f1f5f9',
  '#e2e8f0',
  '#cbd5e1',
  '#94a3b8',
  '#64748b',
  '#475569',
  '#334155',
  '#1e293b',
  '#0f172a',
  '#020617',
];
const TW_GRAY = [
  '#f9fafb',
  '#f3f4f6',
  '#e5e7eb',
  '#d1d5db',
  '#9ca3af',
  '#6b7280',
  '#4b5563',
  '#374151',
  '#1f2937',
  '#111827',
  '#030712',
];
const TW_ZINC = [
  '#fafafa',
  '#f4f4f5',
  '#e4e4e7',
  '#d4d4d8',
  '#a1a1aa',
  '#71717a',
  '#52525b',
  '#3f3f46',
  '#27272a',
  '#18181b',
  '#09090b',
];
const TW_NEUTRAL = [
  '#fafafa',
  '#f5f5f5',
  '#e5e5e5',
  '#d4d4d4',
  '#a3a3a3',
  '#737373',
  '#525252',
  '#404040',
  '#262626',
  '#171717',
  '#0a0a0a',
];
const TW_STONE = [
  '#fafaf9',
  '#f5f5f4',
  '#e7e5e4',
  '#d6d3d1',
  '#a8a29e',
  '#78716c',
  '#57534e',
  '#44403c',
  '#292524',
  '#1c1917',
  '#0c0a09',
];
const TW_RED = [
  '#fef2f2',
  '#fee2e2',
  '#fecaca',
  '#fca5a5',
  '#f87171',
  '#ef4444',
  '#dc2626',
  '#b91c1c',
  '#991b1b',
  '#7f1d1d',
  '#450a0a',
];
const TW_ORANGE = [
  '#fff7ed',
  '#ffedd5',
  '#fed7aa',
  '#fdba74',
  '#fb923c',
  '#f97316',
  '#ea580c',
  '#c2410c',
  '#9a3412',
  '#7c2d12',
  '#431407',
];
const TW_AMBER = [
  '#fffbeb',
  '#fef3c7',
  '#fde68a',
  '#fcd34d',
  '#fbbf24',
  '#f59e0b',
  '#d97706',
  '#b45309',
  '#92400e',
  '#78350f',
  '#451a03',
];
const TW_YELLOW = [
  '#fefce8',
  '#fef9c3',
  '#fef08a',
  '#fde047',
  '#facc15',
  '#eab308',
  '#ca8a04',
  '#a16207',
  '#854d0e',
  '#713f12',
  '#422006',
];
const TW_LIME = [
  '#f7fee7',
  '#ecfccb',
  '#d9f99d',
  '#bef264',
  '#a3e635',
  '#84cc16',
  '#65a30d',
  '#4d7c0f',
  '#3f6212',
  '#365314',
  '#1a2e05',
];
const TW_GREEN = [
  '#f0fdf4',
  '#dcfce7',
  '#bbf7d0',
  '#86efac',
  '#4ade80',
  '#22c55e',
  '#16a34a',
  '#15803d',
  '#166534',
  '#14532d',
  '#052e16',
];
const TW_EMERALD = [
  '#ecfdf5',
  '#d1fae5',
  '#a7f3d0',
  '#6ee7b7',
  '#34d399',
  '#10b981',
  '#059669',
  '#047857',
  '#065f46',
  '#064e3b',
  '#022c22',
];
const TW_TEAL = [
  '#f0fdfa',
  '#ccfbf1',
  '#99f6e4',
  '#5eead4',
  '#2dd4bf',
  '#14b8a6',
  '#0d9488',
  '#0f766e',
  '#115e59',
  '#134e4a',
  '#042f2e',
];
const TW_CYAN = [
  '#ecfeff',
  '#cffafe',
  '#a5f3fc',
  '#67e8f9',
  '#22d3ee',
  '#06b6d4',
  '#0891b2',
  '#0e7490',
  '#155e75',
  '#164e63',
  '#083344',
];
const TW_SKY = [
  '#f0f9ff',
  '#e0f2fe',
  '#bae6fd',
  '#7dd3fc',
  '#38bdf8',
  '#0ea5e9',
  '#0284c7',
  '#0369a1',
  '#075985',
  '#0c4a6e',
  '#082f49',
];
const TW_BLUE = [
  '#eff6ff',
  '#dbeafe',
  '#bfdbfe',
  '#93c5fd',
  '#60a5fa',
  '#3b82f6',
  '#2563eb',
  '#1d4ed8',
  '#1e40af',
  '#1e3a8a',
  '#172554',
];
const TW_INDIGO = [
  '#eef2ff',
  '#e0e7ff',
  '#c7d2fe',
  '#a5b4fc',
  '#818cf8',
  '#6366f1',
  '#4f46e5',
  '#4338ca',
  '#3730a3',
  '#312e81',
  '#1e1b4b',
];
const TW_VIOLET = [
  '#f5f3ff',
  '#ede9fe',
  '#ddd6fe',
  '#c4b5fd',
  '#a78bfa',
  '#8b5cf6',
  '#7c3aed',
  '#6d28d9',
  '#5b21b6',
  '#4c1d95',
  '#2e1065',
];
const TW_PURPLE = [
  '#faf5ff',
  '#f3e8ff',
  '#e9d5ff',
  '#d8b4fe',
  '#c084fc',
  '#a855f7',
  '#9333ea',
  '#7e22ce',
  '#6b21a8',
  '#581c87',
  '#3b0764',
];
const TW_FUCHSIA = [
  '#fdf4ff',
  '#fae8ff',
  '#f5d0fe',
  '#f0abfc',
  '#e879f9',
  '#d946ef',
  '#c026d3',
  '#a21caf',
  '#86198f',
  '#701a75',
  '#4a044e',
];
const TW_PINK = [
  '#fdf2f8',
  '#fce7f3',
  '#fbcfe8',
  '#f9a8d4',
  '#f472b6',
  '#ec4899',
  '#db2777',
  '#be185d',
  '#9d174d',
  '#831843',
  '#500724',
];
const TW_ROSE = [
  '#fff1f2',
  '#ffe4e6',
  '#fecdd3',
  '#fda4af',
  '#fb7185',
  '#f43f5e',
  '#e11d48',
  '#be123c',
  '#9f1239',
  '#881337',
  '#4c0519',
];

const TAILWIND: Palette = {
  id: 'builtin:tailwind',
  name: 'Tailwind',
  kind: 'builtin',
  groups: [
    { label: 'Slate', colors: TW_SLATE },
    { label: 'Gray', colors: TW_GRAY },
    { label: 'Zinc', colors: TW_ZINC },
    { label: 'Neutral', colors: TW_NEUTRAL },
    { label: 'Stone', colors: TW_STONE },
    { label: 'Red', colors: TW_RED },
    { label: 'Orange', colors: TW_ORANGE },
    { label: 'Amber', colors: TW_AMBER },
    { label: 'Yellow', colors: TW_YELLOW },
    { label: 'Lime', colors: TW_LIME },
    { label: 'Green', colors: TW_GREEN },
    { label: 'Emerald', colors: TW_EMERALD },
    { label: 'Teal', colors: TW_TEAL },
    { label: 'Cyan', colors: TW_CYAN },
    { label: 'Sky', colors: TW_SKY },
    { label: 'Blue', colors: TW_BLUE },
    { label: 'Indigo', colors: TW_INDIGO },
    { label: 'Violet', colors: TW_VIOLET },
    { label: 'Purple', colors: TW_PURPLE },
    { label: 'Fuchsia', colors: TW_FUCHSIA },
    { label: 'Pink', colors: TW_PINK },
    { label: 'Rose', colors: TW_ROSE },
  ],
};

// ─── Fias brand ─────────────────────────────────────────────────

/** Small curated set keyed to product surfaces (primary, accent,
 *  semantic colors, neutrals). Kept short on purpose — the brand
 *  palette is meant to surface fast in the picker, not enumerate
 *  every possible variant. */
const FIAS_BRAND: Palette = {
  id: 'builtin:fias-brand',
  name: 'Fias Brand',
  kind: 'builtin',
  groups: [
    {
      label: 'Primary',
      colors: ['#0070f3', '#0050b3', '#003a82', '#7fbcff', '#cfe5ff'],
    },
    {
      label: 'Semantic',
      colors: ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'],
    },
    {
      label: 'Neutral',
      colors: ['#ffffff', '#f3f4f6', '#d1d5db', '#6b7280', '#1f2937', '#000000'],
    },
  ],
};

// Tailwind shade index is unused at runtime — exported for tests
// that want to walk a row and assert ordering / count.
export const TAILWIND_SHADE_KEYS = TW_SHADES;

/** All built-in palettes in their canonical order. The picker
 *  surfaces them in this order in the dropdown; user palettes
 *  appear after them. */
export const BUILTIN_PALETTES: ReadonlyArray<Palette> = [MATERIAL, TAILWIND, FIAS_BRAND];

/** Lookup helper — never returns the original reference. */
export function getBuiltinPalette(id: string): Palette | undefined {
  return BUILTIN_PALETTES.find((p) => p.id === id);
}
